import React, { useEffect, useRef, useState, useCallback } from 'react';
import { computeWindowedImageData, paintTransformed } from '../lib/dicom-renderer.js';
import styles from './DicomCanvas.module.css';

// The canvas owns transform state (pan/zoom/rotate). WC/WW is lifted so the
// right panel can drive presets. tool controls which drag gesture does what.
export default function DicomCanvas({
  parsed,
  tool,              // 'wl' | 'pan' | 'zoom' | 'measure' | null
  wc, ww,
  onWindowChange,    // (wc, ww) => void, fired on live drag
  invert = false,
  rotation = 0,
  overlays,          // { topLeft, topRight, bottomLeft, bottomRight } strings
}) {
  const canvasRef   = useRef(null);
  const offscreenRef = useRef(null);
  const [transform, setTransform] = useState({ zoom: 1, panX: 0, panY: 0 });
  const dragRef = useRef(null);

  // Resize canvas to its container (HiDPI-aware).
  const resize = useCallback(() => {
    const c = canvasRef.current;
    if (!c) return;
    const rect = c.parentElement.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width  = Math.floor(rect.width * dpr);
    c.height = Math.floor(rect.height * dpr);
    c.style.width  = `${rect.width}px`;
    c.style.height = `${rect.height}px`;
    const ctx = c.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }, []);

  // Rebuild the offscreen (windowed) whenever wc/ww/invert/parsed changes.
  useEffect(() => {
    if (!parsed) return;
    const img = computeWindowedImageData(parsed, { wc, ww, invert });
    const off = document.createElement('canvas');
    off.width = parsed.cols;
    off.height = parsed.rows;
    off.getContext('2d').putImageData(img, 0, 0);
    offscreenRef.current = off;
    redraw();
  }, [parsed, wc, ww, invert]);

  // Redraw on transform changes.
  const redraw = useCallback(() => {
    const c = canvasRef.current;
    const off = offscreenRef.current;
    if (!c || !off) return;
    paintTransformed(c, off, { ...transform, rotation });
  }, [transform, rotation]);

  useEffect(() => {
    resize();
    redraw();
    const ro = new ResizeObserver(() => { resize(); redraw(); });
    ro.observe(canvasRef.current.parentElement);
    return () => ro.disconnect();
  }, [resize, redraw]);

  useEffect(redraw, [redraw]);

  // Mouse gestures.
  function onPointerDown(e) {
    dragRef.current = { x: e.clientX, y: e.clientY, wc, ww, zoom: transform.zoom, panX: transform.panX, panY: transform.panY };
    e.currentTarget.setPointerCapture(e.pointerId);
  }
  function onPointerMove(e) {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (tool === 'wl') {
      const newWc = d.wc + dx;
      const newWw = Math.max(1, d.ww + dy);
      onWindowChange?.(newWc, newWw);
    } else if (tool === 'pan') {
      setTransform(t => ({ ...t, panX: d.panX + dx, panY: d.panY + dy }));
    } else if (tool === 'zoom') {
      const factor = Math.exp(dy * -0.005);
      setTransform(t => ({ ...t, zoom: Math.max(0.1, Math.min(20, d.zoom * factor)) }));
    }
  }
  function onPointerUp(e) {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  }
  function onWheel(e) {
    if (!parsed) return;
    e.preventDefault();
    const factor = Math.exp(-e.deltaY * 0.001);
    setTransform(t => ({ ...t, zoom: Math.max(0.1, Math.min(20, t.zoom * factor)) }));
  }

  return (
    <div className={styles.canvasWrap} onWheel={onWheel}>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />
      {overlays?.topLeft     && <div className={`${styles.overlay} ${styles.tl}`}>{overlays.topLeft}</div>}
      {overlays?.topRight    && <div className={`${styles.overlay} ${styles.tr}`}>{overlays.topRight}</div>}
      {overlays?.bottomLeft  && <div className={`${styles.overlay} ${styles.bl}`}>{overlays.bottomLeft}</div>}
      {overlays?.bottomRight && <div className={`${styles.overlay} ${styles.br}`}>{overlays.bottomRight}</div>}
      <div className={`${styles.overlay} ${styles.watermark}`}>FOR VIEWING ONLY · NOT FOR PRIMARY DIAGNOSIS</div>
    </div>
  );
}
